<template>
  <div class="bg-white container">
    <div class="py-4 flex items-center justify-between relative">
    <RouterLink to="/">
      <img :src='logo' alt="WUSA Clubs Logo" sizes="400px md:500px" />
    </RouterLink>
    <div v-if="authenticated" class="flex items-center gap-3 lg:hidden ml-auto mr-4">
      <NotificationBell />
      <UserMenu :show-name="false" />
    </div>
    <a v-ripple class="cursor-pointer lg:hidden! mt-1 p-ripple fa-solid menu-text-primary"
      v-styleclass="{ selector: '@next', enterFromClass: 'hidden', leaveToClass: 'hidden', hideOnOutsideClick: true }"
      id="hamburger">
    </a>
    <div
    :class="['mobile-menu items-center menu-bg grow absolute lg:static w-full left-0 top-full px-6 lg:px-0 z-20 shadow-lg lg:shadow-none lg:flex lg:items-center lg:justify-between', { hidden: isMenuHidden }]">
      <ul
        class="list-none px-3 py-0 md:p-0 md:m-0 flex lg:items-center select-none flex-col lg:flex-row cursor-pointer">
        <li v-for="item in menuItems" :key="item.label">
          <component :is="item.href ? 'a' : RouterLink" v-bind="linkAttrs(item)"
            :data-test-id="toKebabCase(item.label)"
            class="flex px-0 lg:px-3 xl:px-5 py-3 font-medium transition-colors duration-150 p-ripple text-primary hover:text-primary-dark" @click="hideMenu">
            <span>{{ item.label }}</span>
          </component>
        </li>
      </ul>

      <div class="flex justify-between lg:ml-auto border-t lg:border-t-0 border-gray-200 py-3 lg:py-0 mt-3 lg:mt-0">
        <Button v-if="!authenticated" v-slot="slotProps" asChild outlined>
          <router-link
            :to="{ name: 'login' }"
            data-testid="button-signin-club"
            :class="[slotProps.class, 'p-button-primary font-bold w-full uppercase']"
          >
            Sign In to Clubs
          </router-link>
        </Button>

        <template v-else>
          <div class="flex flex-col lg:flex-row items-start lg:items-center gap-3">
            <NotificationBell class="hidden lg:flex" />
            <UserMenu class="hidden lg:inline-flex" />
            <RouterLink :to="{name:'manage-club'}" @click="hideMenu">
              <Button class="p-button-primary w-full"
                      outlined
                      icon="fa-solid fa-user-group"
                      label="Manage My Clubs"
                      data-testid="button-manage-my-club" />
            </RouterLink>
          </div>
        </template>
      </div>
    </div>
    </div>
  </div>
</template>

<script setup>
import { ref, nextTick, computed, watch } from "vue";
import { toKebabCase } from '@/utils/string-utils'
import { RouterLink, useRoute } from 'vue-router'
import Button from 'primevue/button';
import Logo from '@/images/clubs-logo-primary.svg';
import NotificationBell from '@/components/notifications/NotificationBell.vue';
import UserMenu from '@/components/common/UserMenu.vue';

import { useUserStore } from '@/stores/user';

const userStore = useUserStore();
const authenticated = computed(() => userStore.isLoggedIn);

const menuItems = [
  {
    label: 'Clubs Directory',
    to: '/find-a-club'
  },
  {
    label: 'New Club Application',
    to: '/start-a-club'
  },
  {
    label: 'Resources',
    to: '/resources'
  },
  {
    label: 'FAQ',
    to: '/faq'
  },
  {
    label: 'Important Dates',
    href: 'https://wusa.ca/clubs/important-dates/'
  }
];

// RouterLink resolves `to` against the app base, so external URLs need a plain <a>
const linkAttrs = (item) =>
  item.href
    ? { href: item.href, target: '_blank', rel: 'noopener noreferrer' }
    : { to: item.to };

const isMenuHidden = ref(true);
const route = useRoute();

watch(() => route.fullPath, () => {
  isMenuHidden.value = true;
});

const props = defineProps({
  appName: {
    type: String,
    default: 'Wusa Clubs'
  },
  logo: {
    type: String,
    default: Logo
  },
});

// limitation to v-styleclass directive can't use @click event
// retoggle the class to hide the menu
const hideMenu = async () => {
  isMenuHidden.value = false;
  await nextTick();
  isMenuHidden.value = true;
}
</script>

<style scoped>
/* The modified code adds CSS styles to change the content of the #hamburger element based on the visibility of the adjacent .mobile-menu element.
If the .mobile-menu element is hidden, the content of #hamburger will be set to "\f0c9".
If the .mobile-menu element is not hidden, the content of #hamburger will be set to "\f00d". */
#hamburger:has(+ .mobile-menu.hidden):before {
  content: "\f0c9";
}

#hamburger:not(#hamburger:has(+ .mobile-menu.hidden)):before {
  content: "\f00d";
}

.menu-text-primary {
  color: var(--p-wusa-navy-primary);
}

.menu-text-primary:hover {
  color: var(--p-wusa-navy-500);
}

.menu-bg {
  background-color: var(--p-wusa-white);
}
</style>
